import React, { useState, useEffect } from 'react';
import {
  TextField,
  Checkbox,
  FormControlLabel,
  Button,
  Box,
  FormHelperText,
  Alert
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import InputAdornment from '@mui/material/InputAdornment';
import { buildGeneralInformationPayload } from '../../services/payloads/generalInformationPayload';
import { fetchApi } from '../../services/api';
import { setEightDInfoId } from '../../services/eightDInfoStore';
import TwoMonthDatePicker from '../../sharedcomponents/two-month-datepicker/TwoMonthDatePicker';
import { useSanitizedForm } from '../../hooks/useSanitizedInput';
import './generalinformation.css';


const GeneralInformation = ({ onChildChanged, formModeUpdate, reportData, generalInfoSaved, readOnly }) => {
  // Initialize sanitized form with field types
  const fieldTypes = {
    reportNo: 'alphanumericWithBasicPunct',
    problemStatement: 'textarea',
    partDescription: 'textarea',
    sourceOfComplain: 'textarea',
    partNo: 'alphanumericWithBasicPunct',
    supplierName: 'alphanumericWithBasicPunct',
    severityPoints: 'number'
  };

  const {
    formData,
    formErrors,
    isFormValid,
    updateField,
    validateForm,
    resetForm,
    getFieldProps
  } = useSanitizedForm({
    reportNo: '',
    problemStatement: '',
    partDescription: '',
    sourceOfComplain: '',
    partNo: '',
    startDate: null,
    supplierName: '',
    severityPoints: '',
    isRecurringIssue: false,
    isCustomerSafetyEffect: false,
    momId: '',
    momMatterId: ''
  }, fieldTypes);

  // Track if we're in edit mode (vs view mode) when formModeUpdate is true
  const [isEditing, setIsEditing] = useState(false);

  // Load report data when in edit mode
  useEffect(() => {
    if (reportData && formModeUpdate) {
      // Update each field through the sanitized form
      Object.keys(reportData).forEach(key => {
        if (reportData[key] !== null && reportData[key] !== undefined) {
          updateField(key, reportData[key]);
        }
      });
    }
  }, [reportData, formModeUpdate, updateField]);

  // Error state
  const [errors, setErrors] = useState({});
  const [isSaveBtnDisabled, setIsSaveBtnDisabled] = useState(true);

  // Save state & notices
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saveNotice, setSaveNotice] = useState(null);

  //error message name in pascal case
  const toPascalCase = (str) => {
    return str
      .replace(/([A-Z])/g, ' $1') // Add space before capital letters
      .split(' ')                  // Split into words
      .map(word => word.charAt(0).toUpperCase() + word.slice(1)) // Capitalize each word
      .join(' ');                  // Join back with spaces
  };

  // Handle form field changes with sanitization
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;
    
    if (type === 'checkbox') {
      // For checkboxes, update directly
      updateField(name, newValue);
    } else {
      // For text inputs, use sanitized update
      const result = updateField(name, newValue);
      
      // Additional validation for severity points
      if (name === 'severityPoints' && newValue && !/^[1-9]$/.test(newValue)) {
        setErrors(prev => ({ ...prev, [name]: 'Only numeric value allowed, between 1 to 9' }));
      } else {
        setErrors(prev => ({ ...prev, [name]: '' }));
      }
    }
  };


  // Handle date change with real-time validation
  const handleDateChange = (date) => {
    updateField('startDate', date);
    setErrors((prev) => ({ ...prev, startDate: date ? '' : 'Start date is required' }));
  };


  // Validation function with sanitization check
  const validate = () => {
    const validationResult = validateForm();
    
    if (!validationResult.isValid) {
      const allErrors = Object.values(validationResult.errors).flat();
      setSaveError(`Validation failed: ${allErrors.join(', ')}`);
      return false;
    }

    const newErrors = {};

    // Required fields validation
    if (!formData.problemStatement) newErrors.problemStatement = 'Problem Statement is required';
    if (!formData.partDescription) newErrors.partDescription = 'Part Description is required';
    if (!formData.sourceOfComplain) newErrors.sourceOfComplain = 'Source of Complain is required';
    if (!formData.partNo) newErrors.partNo = 'Part Number is required';
    if (!formData.startDate) newErrors.startDate = 'Start date is required';
    if (!formData.supplierName) newErrors.supplierName = 'Supplier Name is required';

    // Severity points validation
    if (!formData.severityPoints) {
      newErrors.severityPoints = 'Severity Points is required';
    } else if (!/^[1-9]$/.test(formData.severityPoints)) {
      newErrors.severityPoints = 'Only numeric value allowed, between 1 to 9';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };


  // Handle form submission
  const handleSave = async () => {
    console.log('[Frontend] GeneralInformation handleSave started');
    console.log('[Frontend] Form data being saved:', JSON.stringify(formData, null, 2));
    
    setSaveError(null);
    setSaveNotice(null);

    if (!validate()) {
      console.log('[Frontend] Validation failed');
      setSaveError('Please enter all mandatory fields');
      return;
    }
    
    setSaving(true);
    try {
      const payload = buildGeneralInformationPayload(formData);
      console.log('[Frontend] GeneralInformation payload being sent:', JSON.stringify(payload, null, 2));
      
      const response = await fetchApi('/api/EightDHeader/Create8DHeader', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      
      console.log('[Frontend] GeneralInformation API response:', JSON.stringify(response, null, 2));
      
      // Store the EightDInfoId for use in other components
      const respData = response?.data?.data || response?.data || response;
      const eightDInfoId = respData?.eightDInfoId || respData?.EightDInfoId;
      const reportNo = respData?.reportNo || respData?.ReportNo || respData?.report_no || '';
      
      console.log('[Frontend] Extracted values:');
      console.log('  - eightDInfoId:', eightDInfoId);
      console.log('  - reportNo:', reportNo);
      
      if (eightDInfoId) {
        setEightDInfoId(eightDInfoId);
        console.log('[Frontend] EightDInfoId stored in global store:', eightDInfoId);
      }
      
      const updatedFormData = { ...formData, reportNo: reportNo || '' };
      updateField('reportNo', reportNo || '');
      setSaveNotice('General information saved successfully.');
      
      console.log('[Frontend] Calling onChildChanged with:', { ...updatedFormData, eightDInfoId, reportNo });
      onChildChanged({ ...updatedFormData, eightDInfoId, reportNo });
      
      console.log('[Frontend] GeneralInformation save completed successfully');
    } catch (error) {
      console.error('[Frontend] GeneralInformation save error:', error);
      setSaveError(error?.message || 'Failed to save general information.');
    } finally {
      setSaving(false);
    }
  };



  // Disable Save button if any required field is empty or has errors
  useEffect(() => {
    const requiredFields = [
      formData.problemStatement,
      formData.partDescription,
      formData.sourceOfComplain,
      formData.partNo,
      formData.startDate,
      formData.supplierName,
      formData.severityPoints
    ];

    const hasErrors = Object.values(errors).some((err) => err) || !isFormValid;
    setIsSaveBtnDisabled(requiredFields.some((field) => !field) || hasErrors);
  }, [formData, errors, isFormValid]);

  // Determine if fields should show as read-only text
  const showReadOnly = (formModeUpdate || generalInfoSaved) && !isEditing;

  return (
    <div className="container-fluid">
      <h2>General Information</h2>
      <div className="formGroup">
        <form className={`general-form ${showReadOnly ? 'updatemode' : ''}`}>
          <div className="reportsField">
            {(formModeUpdate || generalInfoSaved || formData.reportNo) && (
              <div className="col-12 col-sm-12 col-md-12 float-left">
                <div style={{ marginBottom: '16px', fontFamily: 'GEG Body Copy, sans-serif' }}>
                  <div><strong>8D Report No.</strong></div>
                  <div>{formData.reportNo}</div>
                </div>
              </div>
            )}
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 3 }} className="input-compact">

              {showReadOnly ? (
                <div className="col-3 col-sm-12 col-md-4 float-left">
                  <div style={{ marginBottom: '16px', fontFamily: 'GEG Body Copy, sans-serif' }}>
                    <div><strong>Problem Statement</strong></div>
                    <div>{formData.problemStatement}</div>
                  </div>
                </div>
              ) : (
                <TextField className="col-3 col-sm-12 col-md-4 float-left"
                  label="Problem Statement"
                  name="problemStatement"
                  value={formData.problemStatement}
                  onChange={handleChange}
                  fullwidth
                  variant="standard"
                  rows={1}
                  error={!!errors.problemStatement}
                  helperText={errors.problemStatement}
                  sx={{
                    '& .MuiInput-underline:after': {
                      borderBottomColor: '#800040 !important',
                    },
                    '& .MuiInput-underline:before': {
                      borderBottomColor: '#ccc',
                    },
                    '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
                      borderBottomColor: '#800040',
                    },
                    '& .MuiInputBase-root:after': {
                      borderBottomColor: '#800040 !important',
                    }
                  }}
                />
              )}

              {showReadOnly ? (
                <div className="col-12 col-sm-12 col-md-4 float-left">
                  <div style={{ marginBottom: '16px', fontFamily: 'GEG Body Copy, sans-serif' }}>
                    <div><strong>Part Description</strong></div>
                    <div>{formData.partDescription}</div>
                  </div>
                </div>
              ) : (
                <div className="col-12 col-sm-12 col-md-4 float-left">
                  <TextField
                    label="Part Description"
                    name="partDescription"
                    value={formData.partDescription}
                    onChange={handleChange}
                    fullWidth
                    variant="standard"
                    rows={1}
                    error={!!errors.partDescription}
                    helperText={errors.partDescription}
                    sx={{
                      '& .MuiInput-underline:after': {
                        borderBottomColor: '#800040 !important',
                      },
                      '& .MuiInput-underline:before': {
                        borderBottomColor: '#ccc',
                      },
                      '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
                        borderBottomColor: '#800040',
                      },
                      '& .MuiInputBase-root:after': {
                        borderBottomColor: '#800040 !important',
                      }
                    }}
                  />
                </div>
              )}

              {showReadOnly ? (
                <div className="col-12 col-sm-12 col-md-4 float-left">
                  <div style={{ marginBottom: '16px', fontFamily: 'GEG Body Copy, sans-serif' }}>
                    <div><strong>Source of Complain</strong></div>
                    <div>{formData.sourceOfComplain}</div>
                  </div>
                </div>
              ) : (
                <div className="col-12 col-sm-12 col-md-4 float-left">
                  <TextField
                    label="Source of Complain"
                    name="sourceOfComplain"
                    value={formData.sourceOfComplain}
                    onChange={handleChange}
                    fullWidth
                    variant="standard"
                    rows={1}
                    error={!!errors.sourceOfComplain}
                    helperText={errors.sourceOfComplain}
                    sx={{
                      '& .MuiInput-underline:after': {
                        borderBottomColor: '#800040 !important',
                      },
                      '& .MuiInput-underline:before': {
                        borderBottomColor: '#ccc',
                      },
                      '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
                        borderBottomColor: '#800040',
                      },
                      '& .MuiInputBase-root:after': {
                        borderBottomColor: '#800040 !important',
                      }
                    }}
                  />
                </div>
              )}
            </Box>
            <Box sx={{ height: 16 }} />
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 3 }} className="input-compact">
              {showReadOnly ? (
                <div className="col-12 col-sm-6 col-md-4 float-left">
                  <div style={{ marginBottom: '16px', fontFamily: 'GEG Body Copy, sans-serif' }}>
                    <div><strong>Part Number</strong></div>
                    <div>{formData.partNo}</div>
                  </div>
                </div>
              ) : (
                <div className="col-12 col-sm-6 col-md-4 float-left">
                  <TextField
                    label="Part Number"
                    name="partNo"
                    value={formData.partNo}
                    onChange={handleChange}
                    fullWidth
                    variant="standard"
                    rows={2}
                    error={!!errors.partNo}
                    helperText={errors.partNo}
                    sx={{
                      '& .MuiInput-underline:after': {
                        borderBottomColor: '#800040 !important',
                      },
                      '& .MuiInput-underline:before': {
                        borderBottomColor: '#ccc',
                      },
                      '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
                        borderBottomColor: '#800040',
                      },
                      '& .MuiInputBase-root:after': {
                        borderBottomColor: '#800040 !important',
                      }
                    }}
                  />
                </div>
              )}

              {showReadOnly ? (
                <div className="col-12 col-sm-6 col-md-4 float-left">
                  <div style={{ marginBottom: '16px', fontFamily: 'GEG Body Copy, sans-serif' }}>
                    <div><strong>Start Date</strong></div>
                    <div>{formData.startDate ? new Date(formData.startDate).toLocaleDateString('en-GB') : ''}</div>
                  </div>
                </div>
              ) : (
                <TwoMonthDatePicker
                  className="col-12 col-sm-6 col-md-4 float-left"
                  label="Start Date"
                  value={formData.startDate}
                  onChange={handleDateChange}
                  error={!!errors.startDate}
                  helperText={errors.startDate}
                />
              )}

              {showReadOnly ? (
                <div className="col-12 col-sm-12 col-md-4 float-left">
                  <div style={{ marginBottom: '16px', fontFamily: 'GEG Body Copy, sans-serif' }}>
                    <div><strong>Supplier Name</strong></div>
                    <div>{formData.supplierName}</div>
                  </div>
                </div>
              ) : (
                <div className="col-12 col-sm-12 col-md-4 float-left">
                  <TextField
                    label="Supplier Name"
                    name="supplierName"
                    value={formData.supplierName}
                    onChange={handleChange}
                    fullWidth
                    variant="standard"
                    rows={1}
                    error={!!errors.supplierName}
                    helperText={errors.supplierName}
                    sx={{
                      '& .MuiInput-underline:after': {
                        borderBottomColor: '#800040 !important',
                      },
                      '& .MuiInput-underline:before': {
                        borderBottomColor: '#ccc',
                      },
                      '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
                        borderBottomColor: '#800040',
                      },
                      '& .MuiInputBase-root:after': {
                        borderBottomColor: '#800040 !important',
                      }
                    }}
                  />
                </div>
              )}
            </Box>
            <Box sx={{ height: 16 }} />
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 3 }}>
              {showReadOnly ? (
                <div className="col-12 col-sm-12 col-md-4 float-left input-compact">
                  <div style={{ marginBottom: '16px', fontFamily: 'GEG Body Copy, sans-serif' }}>
                    <div><strong>Severity (Points)</strong></div>
                    <div>{formData.severityPoints}</div>
                  </div>
                </div>
              ) : (
                <div className="col-12 col-sm-12 col-md-4 float-left input-compact">
                  <TextField
                    label="Severity (Points)"
                    name="severityPoints"
                    value={formData.severityPoints}
                    onChange={handleChange}
                    error={!!errors.severityPoints}
                    helperText={errors.severityPoints}
                    variant="standard"
                    fullWidth
                    inputProps={{ maxLength: 1 }}
                    sx={{
                      '& .MuiInput-underline:after': {
                        borderBottomColor: '#800040 !important',
                      },
                      '& .MuiInput-underline:before': {
                        borderBottomColor: '#ccc',
                      },
                      '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
                        borderBottomColor: '#800040',
                      },
                      '& .MuiInputBase-root:after': {
                        borderBottomColor: '#800040 !important',
                      }
                    }}
                  />
                </div>
              )}

              {showReadOnly ? (
                <div className="col-12 col-sm-6 col-md-4 float-left">
                  <div style={{ marginBottom: '16px', fontFamily: 'GEG Body Copy, sans-serif' }}>
                    <div><strong>Recurring Issue</strong></div>
                    <div>{formData.isRecurringIssue ? 'Yes' : 'No'}</div>
                  </div>
                </div>
              ) : (
                <div className="col-12 col-sm-6 col-md-4 float-left">
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="isRecurringIssue"
                        checked={formData.isRecurringIssue}
                        onChange={handleChange}
                        sx={{
                          color: '#ccc',
                          '&.Mui-checked': {
                            color: '#800040',
                          },
                          '&:hover': {
                            backgroundColor: 'rgba(128, 0, 64, 0.04)',
                          }
                        }}
                      />
                    }
                    label="Recurring Issue?"
                  />
                </div>
              )}

              {showReadOnly ? (
                <div className="col-12 col-sm-6 col-md-4 float-left pb-5 recurring">
                  <div style={{ marginBottom: '16px', fontFamily: 'GEG Body Copy, sans-serif' }}>
                    <div><strong>Customer Safety Effect</strong></div>
                    <div>{formData.isCustomerSafetyEffect ? 'Yes' : 'No'}</div>
                  </div>
                </div>
              ) : (
                <div className="col-12 col-sm-6 col-md-4 float-left pb-5 recurring">
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="isCustomerSafetyEffect"
                        checked={formData.isCustomerSafetyEffect}
                        onChange={handleChange}
                        sx={{
                          color: '#ccc',
                          '&.Mui-checked': {
                            color: '#800040',
                          },
                          '&:hover': {
                            backgroundColor: 'rgba(128, 0, 64, 0.04)',
                          }
                        }}
                      />
                    }
                    label="Customer Safety Effect"
                  />
                </div>
              )}
            </Box>

            {formData.momId && (
              <div className="col-12 col-sm-6 col-md-4 float-left">
                <label>MOM ID / {formData.momId}</label>
              </div>
            )}

            {formData.momMatterId && (
              <div className="col-12 col-sm-6 col-md-4 float-left">
                <label>MATTER ID / {formData.momMatterId}</label>
              </div>
            )}
          </div>

          {!formModeUpdate && !generalInfoSaved && (
            <div className="formButtons">
              <Button               
                variant="contained"
                color="primary"
                onClick={handleSave}
                disabled={saving || generalInfoSaved}
              >
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </div>
          )}

          {saveError && (
            <Box sx={{ mt: 2 }}>
              <Alert severity="error">{saveError}</Alert>
            </Box>
          )}
          {saveNotice && (
            <Box sx={{ mt: 2 }}>
              <Alert severity="success">{saveNotice}</Alert>
            </Box>
          )}


        </form>
      </div>
    </div>
  );
};

export default GeneralInformation;
